import { createReducer, getType } from "typesafe-actions";

import DateRangeType from "@mapmycustomers/shared/enum/DateRangeType";
import LeaderboardMetricFieldName from "@mapmycustomers/shared/enum/fieldModel/LeaderboardMetricFieldName";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import { AnyEntity, EntityType } from "@mapmycustomers/shared/types/entity";
import LeaderboardItem from "@mapmycustomers/shared/types/entity/LeaderboardItem";
import DateRange from "@mapmycustomers/shared/types/range/DateRange";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";

import MapFilter from "@app/scene/reports/enums/MapFilter";
import { EmailActivityActions } from "@app/scene/reports/store/emailActivity/actions";
import emailActivity, {
  emailActivityInitialState,
  EmailActivityState,
} from "@app/scene/reports/store/emailActivity/reducers";
import getDefaultSelectedUsersState from "@app/scene/reports/store/getDefaultSelectedUsersState";
import { MileageTrackingActions } from "@app/scene/reports/store/mileageTracking/actions";
import mileageTracking, {
  mileageTrackingInitialState,
  MileageTrackingState,
} from "@app/scene/reports/store/mileageTracking/reducers";
import { PermissionsReportActions } from "@app/scene/reports/store/permissionsReport/actions";
import permissionsReport, {
  permissionsReportInitialState,
  PermissionsReportState,
} from "@app/scene/reports/store/permissionsReport/reducers";
import dateRangeTypeToDateRange from "@app/scene/reports/utils/dateRangeTypeToDateRange";
import { Actions as AppActions } from "@app/store/app/actions";
import Report from "@app/types/Report";
import LeaderboardViewState from "@app/types/viewModel/LeaderboardViewState";
import getColumnsVisibleByDefault from "@app/util/fieldModel/getColumnsVisibleByDefault";
import leaderboardFieldModel from "@app/util/fieldModel/LeaderboardFieldModel";
import { ReportFieldName } from "@app/util/fieldModel/ReportFieldModel";
import {
  createEmptyFilterModel,
  createSimpleCondition,
  getSimpleConditionForField,
  removeSimpleConditionForFields,
  upsertSimpleConditions,
} from "@app/util/filters/utils";
import standardRangeToDateRange from "@app/util/range/standardRangeToDateRange";

import FilterType from "../components/Export/FilterType";
import ReportsDateRangeType from "../enums/DateRangeType";
import LeaderboardMetricsData from "../types/LeaderboardMetricsData";
import { DEFAULT_EXPORT_PAGE_SIZE } from "../utils/const";

import {
  Actions,
  applyPreviewViewSettings,
  changeReportDescription,
  changeReportId,
  changeReportName,
  changeReportType,
  deleteReport,
  downloadModalData,
  downloadReport,
  editReport,
  fetchActivityCardData,
  fetchActivityCardListData,
  fetchDealsByStage,
  fetchDealsGeoChartData,
  fetchDealsMapData,
  fetchExportCardData,
  fetchExtraInfo,
  fetchFunnelsStats,
  fetchLeaderboardData,
  fetchLeaderboardMetricsData,
  fetchModalPreviewData,
  fetchOverviewCardData,
  fetchOverviewMapDeals,
  fetchPreviewData,
  fetchReport,
  fetchReportRecordsCountInfo,
  initializeCreateReport,
  saveReport,
  saveScorecard,
  scheduleReport,
  setDateRange,
  setDateRangeType,
  setUserIds,
  updateReport,
} from "./actions";
import ActivityCardData from "./ActivityCardData";
import DealsGeoChartData from "./DealsGeoChartData";
import DealsMapData from "./DealsMapData";
import DealsProgressionChartData from "./DealsProgressionChartData";
import ExportCardData from "./ExportCardData";
import FunnelStatsData from "./FunnelStatsData";
import getDefaultDateRangeState from "./getDefaultDateRangeState";
import getDefaultReportPreviewViewState from "./getDefaultReportPreviewViewState";
import OverviewData, { OverviewMapData } from "./OverviewData";
import UsageCardData from "./UsageCardData";

const getNewReport = (): Readonly<Partial<Report>> => ({
  id: 0,
  customFields: [],
  description: "",
  name: "",
  selectedColumns: getColumnsVisibleByDefault(EntityType.COMPANY).map(
    (field) => field.exportColumnName
  ),
  selectedFilters: {},
  tableName: EntityType.COMPANY,
});

export interface ReportState {
  activityCardData: ActivityCardData;
  dateRange?: DateRange;
  dateRangeType: ReportsDateRangeType;
  dealsGeoChartData: DealsGeoChartData;
  dealsMapData: DealsMapData;
  dealsProgressionChartData: DealsProgressionChartData;
  duplicateDashboardId?: string;
  emailActivity: EmailActivityState;
  error: undefined | unknown;
  exportCardData: ExportCardData;
  extraInfo: AnyEntity | undefined;
  funnelStatsData: FunnelStatsData;
  leaderboard: {
    loading?: boolean;
    metaData?: { lastRunAt?: string };
    metricsItems: Partial<Record<LeaderboardMetricFieldName, LeaderboardMetricsData>>;
    metricsLoading?: boolean;
    metricsViewState: {
      teamIds?: Team["id"][];
      userId?: User["id"];
    };
    tableItems: LeaderboardItem[];
    viewState: LeaderboardViewState;
  };
  loading: boolean;
  mileageTracking: MileageTrackingState;
  newDashboardId?: string;
  overviewData: OverviewData;
  overviewMapData: OverviewMapData;
  permissionsReport: PermissionsReportState;
  reportRecordsCountLoading: boolean;
  reportSaving: boolean;
  scorecardSaving: boolean;
  usageCardData: UsageCardData;
  userIds?: User["id"][];
}

const initialState: ReportState = {
  activityCardData: {
    activities: [],
    activitiesForMap: [],
    companies: [],
    companiesForMap: [],
    dateRange: dateRangeTypeToDateRange(ReportsDateRangeType.YESTERDAY)!,
    listPageNumber: 0,
    mapFilter: MapFilter.COMPLETED_ACTIVITIES,
    people: [],
    peopleForMap: [],
    pins: [],
    totalActivities: 0,
    totalCompanies: 0,
    totalPeople: 0,
    userIds: [], // this is not very accurate, but since we only use this after fetchActivityCardData is called, we're fine
  },
  ...getDefaultDateRangeState(),
  dealsGeoChartData: {
    dealsCountByHcKey: {},
    loading: false,
  },
  dealsMapData: {
    deals: [],
    filter: MapFilter.COMPLETED_ACTIVITIES,
    loading: false,
    pins: [],
    total: 0,
    userIds: [], // this is not very accurate, but since we only use this after fetchDealsMapData is called, we're fine
  },
  dealsProgressionChartData: {
    dealsError: undefined,
    dealsLoading: false,
    stageToDealsMap: {},
    stageToDealsTotalMap: {},
  },
  emailActivity: emailActivityInitialState,
  error: undefined,
  exportCardData: {
    currentReport: undefined,
    reportListViewState: {
      columns: [],
      filter: createEmptyFilterModel(),
      range: { endRow: DEFAULT_EXPORT_PAGE_SIZE, startRow: 0 },
      sort: [],
    },
    reportPreviewError: undefined,
    reportPreviewLoading: false,
    reports: [],
    reportSaving: false,
    reportSavingError: undefined,
    reportTotalRowsCount: 0,
    total: 0,
    viewState: {
      columns: [],
      filter: createEmptyFilterModel(),
      range: { endRow: DEFAULT_EXPORT_PAGE_SIZE, startRow: 0 },
      sort: [],
    },
  },
  extraInfo: undefined,
  funnelStatsData: {
    loading: false,
    orgStats: {},
    stageSummary: [],
  },
  leaderboard: {
    metricsItems: {},
    metricsViewState: {},
    tableItems: [],
    viewState: {
      activityTypeIds: undefined,
      columns: leaderboardFieldModel.getDefaultListViewState().columns,
      dateRange: standardRangeToDateRange(`${DateRangeType.WEEK}0`)!,
      dateRangeType: `${DateRangeType.WEEK}0`,
      grouping: "team",
      rankingMetric: leaderboardFieldModel.getByName(
        LeaderboardMetricFieldName.ACTIVITIES_COMPLETED
      )!,
      teamIds: undefined,
    },
  },
  loading: false,
  mileageTracking: mileageTrackingInitialState,
  overviewData: {
    activityTypesWithCount: [],
    closedWonDeals: new Map(),
    dealsPerFunnel: [],
    mapFunnelStages: {},
    maxAmountByStageType: {},
    modalPreviews: {
      downloading: false,
      [EntityType.ACTIVITY]: { items: [], total: 0 },
      [EntityType.COMPANY]: { items: [], total: 0 },
      [EntityType.DEAL]: { items: [], total: 0 },
      [EntityType.PERSON]: { items: [], total: 0 },
      [EntityType.ROUTE]: { items: [], total: 0 },
      error: undefined,
      loading: false,
    },
    numDealsLost: 0,
    numDealsWon: 0,
    parentNumDealsLost: 0,
    parentNumDealsWon: 0,
    parentRevenueLost: 0,
    parentRevenueWon: 0,
    parentStatistics: {},
    recordsOverTime: [],
    revenueLost: 0,
    revenueWon: 0,
    statistics: {},
  },
  overviewMapData: {
    deals: [],
    loading: false,
  },
  permissionsReport: permissionsReportInitialState,
  reportRecordsCountLoading: false,
  reportSaving: false,
  scorecardSaving: false,
  usageCardData: {
    locations: [],
  },
  userIds: getDefaultSelectedUsersState(),
};

const report = createReducer<ReportState, Actions | AppActions>(initialState)
  .handleType(getType(fetchFunnelsStats.request), (state) => ({
    ...state,
    funnelStatsData: {
      ...state.funnelStatsData,
      loading: true,
    },
  }))
  .handleType(getType(fetchFunnelsStats.success), (state, action) => ({
    ...state,
    funnelStatsData: {
      ...state.funnelStatsData,
      ...action.payload,
      loading: false,
    },
  }))
  .handleType(getType(fetchFunnelsStats.failure), (state) => ({
    ...state,
    funnelStatsData: {
      ...state.funnelStatsData,
      loading: false,
    },
  }))
  .handleType(getType(fetchExtraInfo.request), (state) => ({
    ...state,
    error: undefined,
    loading: true,
  }))
  .handleType(getType(fetchExtraInfo.success), (state, action) => ({
    ...state,
    extraInfo: action.payload,
    loading: false,
  }))
  .handleType(getType(fetchExtraInfo.failure), (state, action) => ({
    ...state,
    error: action.payload,
    loading: false,
  }))
  .handleType(getType(fetchOverviewCardData.request), (state) => ({
    ...state,
    error: undefined,
    loading: true,
  }))
  .handleType(getType(fetchOverviewCardData.success), (state, action) => ({
    ...state,
    loading: false,
    overviewData: action.payload,
  }))
  .handleType(getType(fetchOverviewCardData.failure), (state, action) => ({
    ...state,
    error: action.payload,
    loading: false,
  }))
  .handleType(getType(fetchOverviewMapDeals.request), (state) => ({
    ...state,
    overviewMapData: {
      deals: [],
      loading: true,
    },
  }))
  .handleType(getType(fetchOverviewMapDeals.success), (state, { payload }) => ({
    ...state,
    overviewMapData: {
      deals: payload,
      loading: false,
    },
  }))
  .handleType(getType(fetchOverviewMapDeals.failure), (state) => ({
    ...state,
    overviewMapData: {
      deals: [],
      loading: false,
    },
  }))
  .handleType(getType(fetchModalPreviewData.request), (state) => ({
    ...state,
    overviewData: {
      ...state.overviewData,
      modalPreviews: {
        ...state.overviewData.modalPreviews,
        error: undefined,
        loading: true,
      },
    },
  }))
  .handleType(getType(fetchModalPreviewData.success), (state, { payload }) => ({
    ...state,
    overviewData: {
      ...state.overviewData,
      modalPreviews: {
        downloading: false,
        [EntityType.ACTIVITY]:
          payload[EntityType.ACTIVITY] ?? state.overviewData.modalPreviews[EntityType.ACTIVITY],
        [EntityType.COMPANY]:
          payload[EntityType.COMPANY] ?? state.overviewData.modalPreviews[EntityType.COMPANY],
        [EntityType.DEAL]:
          payload[EntityType.DEAL] ?? state.overviewData.modalPreviews[EntityType.DEAL],
        [EntityType.PERSON]:
          payload[EntityType.PERSON] ?? state.overviewData.modalPreviews[EntityType.PERSON],
        [EntityType.ROUTE]:
          payload[EntityType.ROUTE] ?? state.overviewData.modalPreviews[EntityType.ROUTE],
        error: undefined,
        loading: false,
      },
    },
  }))
  .handleType(getType(fetchModalPreviewData.failure), (state, action) => ({
    ...state,
    overviewData: {
      ...state.overviewData,
      modalPreviews: {
        ...state.overviewData.modalPreviews,
        error: action.payload,
        loading: false,
      },
    },
  }))
  .handleType(getType(downloadModalData.request), (state) => ({
    ...state,
    overviewData: {
      ...state.overviewData,
      modalPreviews: {
        ...state.overviewData.modalPreviews,
        downloading: true,
        error: undefined,
      },
    },
  }))
  .handleType(getType(downloadModalData.success), (state) => ({
    ...state,
    overviewData: {
      ...state.overviewData,
      modalPreviews: {
        ...state.overviewData.modalPreviews,
        downloading: false,
        error: undefined,
      },
    },
  }))
  .handleType(getType(downloadModalData.failure), (state, action) => ({
    ...state,
    overviewData: {
      ...state.overviewData,
      modalPreviews: {
        ...state.overviewData.modalPreviews,
        downloading: false,
        error: action.payload,
      },
    },
  }))
  .handleType(getType(fetchActivityCardData.request), (state, { payload }) => ({
    ...state,
    activityCardData: {
      ...state.activityCardData,
      ...payload,
      bounds:
        payload.mapFilter && payload.mapFilter !== state.activityCardData.mapFilter
          ? undefined
          : (payload.bounds ?? state.activityCardData.bounds),
    },
    error: undefined,
    loading: true,
  }))
  .handleType(getType(fetchActivityCardData.success), (state, action) => ({
    ...state,
    activityCardData: {
      ...state.activityCardData,
      ...action.payload,
    },
    loading: false,
  }))
  .handleType(getType(fetchActivityCardData.failure), (state, action) => ({
    ...state,
    error: action.payload,
    loading: false,
  }))
  .handleType(getType(fetchActivityCardListData.request), (state, { payload }) => ({
    ...state,
    activityCardData: {
      ...state.activityCardData,
      listPageNumber: payload,
    },
    error: undefined,
    loading: true,
  }))
  .handleType(getType(fetchActivityCardListData.success), (state, action) => ({
    ...state,
    activityCardData: {
      ...state.activityCardData,
      ...action.payload,
    },
    loading: false,
  }))
  .handleType(getType(fetchActivityCardListData.failure), (state, action) => ({
    ...state,
    error: action.payload,
    loading: false,
  }))
  .handleType(getType(fetchDealsMapData.request), (state, { payload }) => ({
    ...state,
    dealsMapData: {
      ...state.dealsMapData,
      ...payload,
      bounds:
        payload.filter && payload.filter !== state.dealsMapData.filter
          ? undefined
          : (payload.bounds ?? state.dealsMapData.bounds),
      loading: true,
    },
  }))
  .handleType(getType(fetchDealsMapData.success), (state, { payload }) => ({
    ...state,
    dealsMapData: {
      ...state.dealsMapData,
      ...payload,
      loading: false,
    },
    loading: false,
  }))
  .handleType(getType(fetchDealsMapData.failure), (state) => ({
    ...state,
    dealsMapData: {
      ...state.dealsMapData,
      deals: [],
      loading: false,
      pins: [],
      total: 0,
    },
  }))
  .handleType(getType(fetchExportCardData.request), (state, { payload }) => {
    // reset page to the first if we used any filters
    const page =
      payload.search ||
      payload.filterType === FilterType.DOWNLOADED ||
      ("entityType" in payload && payload.entityType !== undefined)
        ? 0
        : payload.page;

    let filter = {
      ...state.exportCardData.reportListViewState.filter,
      conditions: [...state.exportCardData.reportListViewState.filter.conditions],
    };
    if ("filterType" in payload && payload.filterType) {
      if (payload.filterType === FilterType.FAVORITE) {
        filter = upsertSimpleConditions(
          filter,
          createSimpleCondition(ReportFieldName.STARRED, FilterOperator.EQUALS, true)
        );
      } else {
        filter = removeSimpleConditionForFields(filter, ReportFieldName.STARRED);
      }
      if (payload.filterType !== FilterType.DOWNLOADED) {
        filter = removeSimpleConditionForFields(filter, ReportFieldName.LAST_GENERATED_AT);
      } else {
        filter = upsertSimpleConditions(
          filter,
          createSimpleCondition(ReportFieldName.LAST_GENERATED_AT, FilterOperator.NOT_EMPTY)
        );
      }
    }
    if ("search" in payload) {
      if (payload.search) {
        filter = upsertSimpleConditions(
          filter,
          createSimpleCondition(ReportFieldName.NAME, FilterOperator.CONTAINS, payload.search)
        );
      } else {
        filter = removeSimpleConditionForFields(filter, ReportFieldName.NAME);
      }
    }
    if ("entityType" in payload) {
      if (payload.entityType) {
        filter = upsertSimpleConditions(
          filter,
          createSimpleCondition(
            ReportFieldName.TABLE_NAME,
            FilterOperator.EQUALS,
            payload.entityType
          )
        );
      } else {
        filter = removeSimpleConditionForFields(filter, ReportFieldName.TABLE_NAME);
      }
    }
    if ("userIds" in payload && payload.userIds) {
      if (payload.userIds.length) {
        filter = upsertSimpleConditions(
          filter,
          createSimpleCondition(ReportFieldName.USER, FilterOperator.CONTAINS, payload.userIds)
        );
      } else {
        filter = removeSimpleConditionForFields(filter, ReportFieldName.USER);
      }
    }

    return {
      ...state,
      error: undefined,
      exportCardData: {
        ...state.exportCardData,
        reportListViewState: {
          ...state.exportCardData.reportListViewState,
          filter,
          range:
            page === undefined
              ? state.exportCardData.reportListViewState.range
              : {
                  endRow: (page + 1) * DEFAULT_EXPORT_PAGE_SIZE,
                  startRow: page * DEFAULT_EXPORT_PAGE_SIZE,
                },
        },
      },
      loading: true,
    };
  })
  .handleType(getType(fetchExportCardData.success), (state, { payload }) => ({
    ...state,
    exportCardData: {
      ...state.exportCardData,
      reports: payload.reports,
      total: payload.total,
    },
    loading: false,
  }))
  .handleType(getType(fetchExportCardData.failure), (state, action) => ({
    ...state,
    error: action.payload,
    loading: false,
  }))
  .handleType(getType(downloadReport.request), (state) => ({
    ...state,
    error: undefined,
    loading: true,
  }))
  .handleType(getType(downloadReport.success), (state) => ({
    ...state,
    loading: false,
  }))
  .handleType(getType(downloadReport.failure), (state, action) => ({
    ...state,
    error: action.payload,
    loading: false,
  }))
  .handleType(getType(scheduleReport.request), (state) => ({
    ...state,
    error: undefined,
    loading: true,
  }))
  .handleType(getType(scheduleReport.success), (state) => ({
    ...state,
    loading: false,
  }))
  .handleType(getType(scheduleReport.failure), (state, action) => ({
    ...state,
    error: action.payload,
    loading: false,
  }))
  .handleType(getType(deleteReport.request), (state) => ({
    ...state,
    error: undefined,
    loading: true,
  }))
  .handleType(getType(deleteReport.success), (state, action) => ({
    ...state,
    exportCardData: {
      ...state.exportCardData,
      reports: state.exportCardData.reports.filter((report) => report.id !== action.payload),
    },
    loading: false,
  }))
  .handleType(getType(deleteReport.failure), (state, action) => ({
    ...state,
    error: action.payload,
    loading: false,
  }))
  .handleType(getType(fetchReport.request), (state) => ({
    ...state,
    error: undefined,
    loading: true,
  }))
  .handleType(getType(fetchReport.success), (state, action) => ({
    ...state,
    exportCardData: {
      ...state.exportCardData,
      currentReport: action.payload,
    },
    loading: false,
  }))
  .handleType(getType(fetchReport.failure), (state, action) => ({
    ...state,
    error: action.payload,
    loading: false,
  }))
  .handleType(getType(initializeCreateReport), (state) => {
    const newReport = getNewReport();
    return {
      ...state,
      exportCardData: {
        ...state.exportCardData,
        currentReport: newReport,
        viewState: getDefaultReportPreviewViewState(newReport),
      },
    };
  })
  .handleType(getType(editReport), (state, action) => ({
    ...state,
    exportCardData: {
      ...state.exportCardData,
      currentReport: action.payload,
    },
  }))
  .handleType(getType(applyPreviewViewSettings), (state, { payload }) => ({
    ...state,
    exportCardData: {
      ...state.exportCardData,
      viewState: {
        ...state.exportCardData.viewState,
        columns: payload.columns ?? state.exportCardData.viewState.columns,
        filter: payload.filter ?? state.exportCardData.viewState.filter,
        range: payload.range ?? state.exportCardData.viewState.range,
        // only update selectedSavedFilterId when it is explicitly present in a payload (even when it is `undefined`)
        selectedSavedFilterId:
          "selectedSavedFilterId" in payload
            ? payload.selectedSavedFilterId
            : state.exportCardData.viewState.selectedSavedFilterId,
        sort: payload.sort ?? state.exportCardData.viewState.sort,
      },
    },
  }))
  .handleType(getType(fetchDealsByStage.request), (state) => ({
    ...state,
    dealsProgressionChartData: {
      ...state.dealsProgressionChartData,
      dealsLoading: true,
    },
  }))
  .handleType(
    getType(fetchDealsByStage.success),
    (state, { payload: { deals, stageId, total } }) => ({
      ...state,
      dealsProgressionChartData: {
        ...state.dealsProgressionChartData,
        dealsLoading: false,
        stageToDealsMap: {
          ...state.dealsProgressionChartData.stageToDealsMap,
          [stageId]: deals,
        },
        stageToDealsTotalMap: {
          ...state.dealsProgressionChartData.stageToDealsTotalMap,
          [stageId]: total,
        },
      },
    })
  )
  .handleType(getType(fetchDealsByStage.failure), (state, { payload }) => ({
    ...state,
    dealsProgressionChartData: {
      ...state.dealsProgressionChartData,
      dealsError: payload,
      dealsLoading: false,
    },
  }))
  .handleType(getType(fetchDealsGeoChartData.request), (state) => ({
    ...state,
    dealsGeoChartData: {
      ...state.dealsGeoChartData,
      loading: true,
    },
  }))
  .handleType(getType(fetchDealsGeoChartData.success), (state, { payload }) => ({
    ...state,
    dealsGeoChartData: {
      dealsCountByHcKey: payload,
      loading: false,
    },
  }))
  .handleType(getType(fetchDealsGeoChartData.failure), (state) => ({
    ...state,
    dealsGeoChartData: {
      dealsCountByHcKey: {},
      loading: false,
    },
  }))
  .handleType(getType(fetchPreviewData.request), (state) => ({
    ...state,
    exportCardData: {
      ...state.exportCardData,
      reportPreviewError: undefined,
      reportPreviewLoading: true,
    },
  }))
  .handleType(getType(fetchPreviewData.success), (state, action) => ({
    ...state,
    exportCardData: {
      ...state.exportCardData,
      reportPreviewLoading: false,
      reportTotalRowsCount: action.payload.reportTotalRowsCount,
    },
  }))
  .handleType(getType(fetchPreviewData.failure), (state, action) => ({
    ...state,
    exportCardData: {
      ...state.exportCardData,
      reportPreviewError: action.payload,
      reportPreviewLoading: false,
    },
  }))
  .handleType(getType(saveReport.request), (state) => ({
    ...state,
    exportCardData: {
      ...state.exportCardData,
      reportSaving: true,
      reportSavingError: undefined,
    },
  }))
  .handleType(getType(saveReport.success), (state) => ({
    ...state,
    exportCardData: {
      ...state.exportCardData,
      reportSaving: false,
    },
  }))
  .handleType(getType(saveReport.failure), (state, action) => ({
    ...state,
    exportCardData: {
      ...state.exportCardData,
      reportSaving: false,
      reportSavingError: action.payload,
    },
  }))
  .handleType(getType(updateReport.success), (state, { payload }) => {
    const starredCondition = getSimpleConditionForField(
      state.exportCardData.reportListViewState.filter,
      ReportFieldName.STARRED
    );
    const isOnFavoritesTab = !!starredCondition?.value;
    const reports = state.exportCardData.reports
      .map((report) => (report.id === payload.id ? payload : report))
      .filter((report) => !isOnFavoritesTab || report.isStarred);

    return {
      ...state,
      exportCardData: {
        ...state.exportCardData,
        reports,
        // a bit hacky, it's better to re-download the entire page on updates
        total: state.exportCardData.total - (state.exportCardData.reports.length - reports.length),
      },
    };
  })
  .handleType(getType(changeReportType), (state, action) => {
    const updatedReport = state.exportCardData.currentReport
      ? {
          ...state.exportCardData.currentReport,
          customFields: [],
          selectedColumns: getColumnsVisibleByDefault(action.payload).map(
            (field) => field.exportColumnName
          ),
          selectedFilters: {},
          tableName: action.payload,
        }
      : undefined;

    return {
      ...state,
      exportCardData: {
        ...state.exportCardData,
        currentReport: updatedReport,
        viewState: updatedReport
          ? getDefaultReportPreviewViewState(updatedReport)
          : state.exportCardData.viewState,
      },
    };
  })
  .handleType(getType(changeReportDescription), (state, action) => ({
    ...state,
    exportCardData: {
      ...state.exportCardData,
      currentReport: state.exportCardData.currentReport
        ? { ...state.exportCardData.currentReport, description: action.payload }
        : undefined,
    },
  }))
  .handleType(getType(changeReportName), (state, action) => ({
    ...state,
    exportCardData: {
      ...state.exportCardData,
      currentReport: state.exportCardData.currentReport
        ? { ...state.exportCardData.currentReport, name: action.payload }
        : undefined,
    },
  }))
  .handleType(getType(changeReportId), (state, action) => ({
    ...state,
    exportCardData: {
      ...state.exportCardData,
      currentReport: state.exportCardData.currentReport
        ? { ...state.exportCardData.currentReport, id: action.payload }
        : undefined,
    },
  }))
  .handleType(getType(setDateRange), (state, { payload }) => ({
    ...state,
    dateRange: payload,
    dateRangeType: ReportsDateRangeType.CUSTOM,
  }))
  .handleType(getType(setDateRangeType), (state, { payload }) => ({
    ...state,
    dateRange: payload === ReportsDateRangeType.CUSTOM ? state.dateRange : undefined,
    dateRangeType: payload,
  }))
  .handleType(getType(setUserIds), (state, { payload }) => ({
    ...state,
    userIds: payload,
  }))
  .handleType(getType(saveScorecard.request), (state) => ({
    ...state,
    scorecardSaving: true,
  }))
  .handleType([getType(saveScorecard.success), getType(saveScorecard.failure)], (state) => ({
    ...state,
    scorecardSaving: false,
  }))
  .handleType(getType(fetchLeaderboardData.request), (state, { payload }) => ({
    ...state,
    leaderboard: {
      ...state.leaderboard,
      loading: true,
      viewState: {
        ...state.leaderboard.viewState,
        activityTypeIds:
          "activityTypeIds" in payload
            ? payload.activityTypeIds
            : state.leaderboard.viewState.activityTypeIds,
        columns: payload.columns ?? state.leaderboard.viewState.columns,
        dateRange:
          (payload.dateRangeType === DateRangeType.CUSTOM
            ? payload.dateRange
            : payload.dateRangeType
              ? standardRangeToDateRange(payload.dateRangeType)
              : undefined) ?? state.leaderboard.viewState.dateRange,
        dateRangeType: payload.dateRangeType ?? state.leaderboard.viewState.dateRangeType,
        funnelIds:
          "funnelIds" in payload ? payload.funnelIds : state.leaderboard.viewState.funnelIds,
        grouping: payload.grouping ?? state.leaderboard.viewState.grouping,
        rankingMetric: payload.rankingMetric ?? state.leaderboard.viewState.rankingMetric,
        stageIds: "stageIds" in payload ? payload.stageIds : state.leaderboard.viewState.stageIds,
        teamIds: "teamIds" in payload ? payload.teamIds : state.leaderboard.viewState.teamIds,
      },
    },
  }))
  .handleType(getType(fetchLeaderboardData.success), (state, { payload }) => ({
    ...state,
    leaderboard: {
      ...state.leaderboard,
      loading: false,
      metaData: payload?.metaData ?? state.leaderboard.metaData,
      tableItems: payload?.items ?? state.leaderboard.tableItems,
    },
  }))
  .handleType(getType(fetchLeaderboardData.failure), (state) => ({
    ...state,
    leaderboard: {
      ...state.leaderboard,
      loading: false,
    },
  }))
  .handleType(getType(fetchLeaderboardMetricsData.request), (state, { payload }) => ({
    ...state,
    leaderboard: {
      ...state.leaderboard,
      metricsItems: payload.selectedMetrics
        ? Object.fromEntries(payload.selectedMetrics.map((metric) => [metric, undefined]))
        : state.leaderboard.metricsItems,
      metricsLoading: true,
      metricsViewState: {
        ...state.leaderboard.metricsViewState,
        activityTypeIds: payload.activityTypeIds ?? state.leaderboard.viewState.activityTypeIds,
        funnelIds: payload.funnelIds ?? state.leaderboard.viewState.funnelIds,
        stageIds: payload.stageIds ?? state.leaderboard.viewState.stageIds,
        teamIds:
          "teamIds" in payload ? payload.teamIds : state.leaderboard.metricsViewState.teamIds,
        userId:
          "userId" in payload
            ? payload.userId
            : "teamIds" in payload
              ? undefined
              : state.leaderboard.metricsViewState.userId,
      },
    },
  }))
  .handleType(getType(fetchLeaderboardMetricsData.success), (state, { payload }) => ({
    ...state,
    leaderboard: {
      ...state.leaderboard,
      metricsItems: payload,
      metricsLoading: false,
    },
  }))
  .handleType(getType(fetchLeaderboardMetricsData.failure), (state) => ({
    ...state,
    leaderboard: {
      ...state.leaderboard,
      metricsLoading: false,
    },
  }))
  .handleType(getType(fetchReportRecordsCountInfo.request), (state) => ({
    ...state,
    reportRecordsCountLoading: true,
  }))
  .handleType(
    [getType(fetchReportRecordsCountInfo.failure), getType(fetchReportRecordsCountInfo.success)],
    (state) => ({
      ...state,
      reportRecordsCountLoading: false,
    })
  )
  .handleType(
    Object.keys(emailActivity.handlers) as EmailActivityActions["type"][],
    (state: ReportState, action: EmailActivityActions) => ({
      ...state,
      emailActivity: emailActivity(state.emailActivity, action),
    })
  )
  .handleType(
    Object.keys(mileageTracking.handlers) as MileageTrackingActions["type"][],
    (state: ReportState, action: MileageTrackingActions) => ({
      ...state,
      mileageTracking: mileageTracking(state.mileageTracking, action),
    })
  )
  .handleType(
    Object.keys(permissionsReport.handlers) as PermissionsReportActions["type"][],
    (state: ReportState, action: PermissionsReportActions) => ({
      ...state,
      permissionsReport: permissionsReport(state.permissionsReport, action),
    })
  );

export type ReportActions = Actions;
export default report;
